import { useAsyncQuery } from '@client/new/helpers/gql';
import {
  useCreateChallengeMutation,
  useGetProgramChallengesCountQuery,
  useGetProgramWithLiteChallengesQuery,
  useUpdateChallengeMutation,
} from 'generated/graphql';
import {
  ServerLiteChallenge,
  ServerProgramWithLiteChallenge,
  toLiteChallenge,
  toSubmissionsURL,
} from './Challenges.mapper';
import {
  CreateChallengePayload,
  LiteChallenge,
  UpdateChallengePayload,
} from './Challenges.types';
import { Async, F1, toAsyncFetched } from '@client/new/helpers/types';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { upload } from '@client/components/ajax';

type ChallengesModelData = {
  name: string;
  programId: number;
  challenges: Array<LiteChallenge>;
};

type ChallengeModelActions = {
  createChallenge: F1<CreateChallengePayload, Promise<LiteChallenge>>;
  updateChallenge: F1<UpdateChallengePayload, Promise<LiteChallenge>>;
  uploadFile: F1<File, Promise<string>>;
};

export type ChallengesModel = ChallengesModelData & ChallengeModelActions;

export const useChallengesModel = (
  programId: number,
): Async<ChallengesModel> => {
  const creatorId = useInfluencerId();
  const res = useGetProgramWithLiteChallengesQuery({
    variables: { collectionId: programId },
  });

  const { data, refetch } = useAsyncQuery(
    res,
    'collection',
    toChallengesModel({ creatorId }),
  );

  const [createChallengeMutation] = useCreateChallengeMutation();
  const [updateChallengeMutation] = useUpdateChallengeMutation();

  const createChallenge = async (input: CreateChallengePayload) => {
    return createChallengeMutation({
      variables: { input },
    }).then(async (res) => {
      if (res.errors) throw new Error(res.errors[0].message);
      const data = await refetch();
      const c = data.challenges.find(
        (d) => `${d.challengeId}` === `${res.data?.createChallenge.id}`,
      );
      if (!c) return Promise.reject('challenge not created');
      return c;
    });
  };

  const updateChallenge = async (input: UpdateChallengePayload) => {
    return updateChallengeMutation({
      variables: { input },
    }).then(async (res) => {
      if (res.errors) throw new Error(res.errors[0].message);
      const data = await refetch();
      const c = data.challenges.find(
        (d) => `${d.challengeId}` === `${input.id}`,
      );
      if (!c) return Promise.reject('challenge not created');
      return c;
    });
  };

  const uploadFile = async (file: File): Promise<string> => {
    const m = await upload(file, creatorId);

    return `${m.cdn01}${m.image}`;
  };

  if (data.type !== 'fetched') return data;
  return toAsyncFetched({
    ...data.value,

    createChallenge,
    updateChallenge,
    uploadFile,
  });
};

const toChallengesModel =
  ({ creatorId }: { creatorId: number }) =>
  (p: ServerProgramWithLiteChallenge): ChallengesModelData => ({
    name: p.name || '',
    programId: p.id,
    challenges: (p.challenges || [])
      .filter(Boolean)
      .map((c) =>
        toLiteChallenge(toSubmissionsURL({ creatorId, programId: p.id }))(
          c as ServerLiteChallenge,
        ),
      )
      .sort((a, b) => b.createdAt - a.createdAt),
  });

export const useChallengesCount = (programId: number): Async<number> => {
  const res = useGetProgramChallengesCountQuery({
    variables: { collectionId: programId },
  });

  const { data } = useAsyncQuery(
    res,
    'collection',
    (c) => c?.challenges?.length || 0,
  );
  return data;
};
